import React, { useEffect, useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, QrCode, ScanLine, MapPin, Sparkles } from 'lucide-react';
import { Button } from './ui/Button';
interface CatchQrRevealProps {
  /** The reservation code tied to THIS drop — encoded into the QR for staff to scan. */
  userCode: string;
  brand: string;
  title: string;
  brandLogo?: string;
  onClose: () => void;
}
type Slide = 'celebrate' | 'instruct';
const CONFETTI_COLORS = ['#FFCC00', '#6046E8', '#22C55E', '#FF6B6B', '#FFFFFF'];
/**
 * A big, full-screen, gamified "catch pass". It celebrates first (slide 1),
 * then reveals the scannable QR with staff instructions (slide 2). The QR
 * encodes the reservation's userCode, which the staff scanner validates.
 */
export function CatchQrReveal({
  userCode,
  brand,
  title,
  brandLogo,
  onClose
}: CatchQrRevealProps) {
  const [slide, setSlide] = useState<Slide>('celebrate');
  // Auto-advance to the instruction/QR slide after a short celebration.
  useEffect(() => {
    if (slide !== 'celebrate') return;
    const t = setTimeout(() => setSlide('instruct'), 1600);
    return () => clearTimeout(t);
  }, [slide]);
  // Close on Escape for keyboard users.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=12&data=${encodeURIComponent(userCode)}`;
  // Pre-compute a deterministic-ish confetti burst so it doesn't reshuffle on re-render.
  const confetti = useMemo(() => Array.from({
    length: 28
  }).map((_, i) => ({
    id: i,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    angle: i / 28 * Math.PI * 2 + Math.random() * 0.4,
    distance: 120 + Math.random() * 160,
    size: 7 + Math.random() * 9,
    rotate: Math.random() * 360,
    delay: Math.random() * 0.12,
    round: i % 3 === 0
  })), []);
  return <motion.div role="dialog" aria-modal="true" aria-label={`Catch pass for ${brand}`} initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-[60] bg-brand-ice flex flex-col overflow-hidden" data-id="element-322" data-mp="1vexya9fds8lm">
      {/* Soft celebratory wash */}
      <div className="absolute inset-0 bg-brand-lavender/50 pointer-events-none" data-id="element-323" data-mp="2ecs5qj0v2c1w" />

      <button onClick={onClose} aria-label="Close catch pass" className="absolute top-5 right-5 z-20 p-2.5 bg-white hover:bg-brand-lavender rounded-full shadow-sm transition-colors text-brand-navy" data-id="element-324" data-mp="20d98il9l8l4d">
        <X className="w-5 h-5" data-id="element-325" data-mp="oxu9lbxtjj6m" />
      </button>

      <AnimatePresence mode="wait" data-id="element-326" data-mp="p06sbrbehxkq">
        {slide === 'celebrate' ? <motion.div key="celebrate" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.25
      }} className="relative z-10 flex-1 flex flex-col items-center justify-center px-8 text-center" data-id="element-327" data-mp="txyl0njvdnpz">
            {/* Confetti burst */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-328" data-mp="2qq4rujx720s">
              {confetti.map(c => <motion.span key={c.id} initial={{
            x: 0,
            y: 0,
            opacity: 0,
            scale: 0
          }} animate={{
            x: Math.cos(c.angle) * c.distance,
            y: Math.sin(c.angle) * c.distance,
            opacity: [0, 1, 1, 0],
            scale: [0, 1, 1, 0.6],
            rotate: c.rotate
          }} transition={{
            duration: 1.4,
            delay: c.delay,
            ease: 'easeOut'
          }} style={{
            width: c.size,
            height: c.size,
            backgroundColor: c.color,
            borderRadius: c.round ? '9999px' : '2px'
          }} className="absolute" data-id="element-329" data-mp="3tu54dsdvivyx" />)}
            </div>

            <motion.div initial={{
          scale: 0,
          rotate: -25
        }} animate={{
          scale: 1,
          rotate: 0
        }} transition={{
          type: 'spring',
          stiffness: 220,
          damping: 14
        }} className="relative w-28 h-28 rounded-3xl bg-brand-yellow flex items-center justify-center shadow-2xl shadow-brand-yellow/30 mb-7" data-id="element-330" data-mp="321utjgisyhb6">
              <span className="text-6xl" aria-hidden="true" data-id="element-331" data-mp="198ogd5hdjf2z">
                🎣
              </span>
              <motion.span initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.3,
            type: 'spring',
            stiffness: 300
          }} className="absolute -top-3 -right-3 w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-lg" data-id="element-332" data-mp="14eagoqvoilsi">
                <Sparkles className="w-5 h-5 text-brand-yellow" data-id="element-333" data-mp="1j3oucb6afpdp" />
              </motion.span>
            </motion.div>

            <motion.p initial={{
          y: 16,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.18
        }} className="text-brand-purple font-bold uppercase tracking-[0.2em] text-xs mb-2" data-id="element-334" data-mp="1e5n5f9m6kvub">
              Catch reserved
            </motion.p>
            <motion.h2 initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.26
        }} className="font-display text-4xl text-brand-navy leading-tight mb-2" data-id="element-335" data-mp="3rd6262fm4nee">
              Your pass is ready!
            </motion.h2>
            <motion.p initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.34
        }} className="text-brand-navy/65 max-w-xs" data-id="element-336" data-mp="37pyq4b9xzu3h">
              Show this at <span className="font-bold text-brand-navy" data-id="element-337" data-mp="2k0qndo7zt2ab">{brand}</span>{' '}
              to claim your catch.
            </motion.p>

            <motion.button initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.7
        }} onClick={() => setSlide('instruct')} className="mt-9 inline-flex items-center gap-2 bg-brand-purple text-white font-bold rounded-full px-6 py-3 shadow-[0_8px_18px_rgba(96,70,232,0.24)] hover:scale-[1.03] active:scale-[0.98] transition-transform" data-id="element-338" data-mp="2f3gzid094yol">
              <QrCode className="w-5 h-5" data-id="element-339" data-mp="3cs0os7tp7fhp" />
              Show my QR
            </motion.button>
          </motion.div> : <motion.div key="instruct" initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-10 overflow-y-auto" data-id="element-340" data-mp="3lq2taxcn4q9q">
            {/* Brand context */}
            <div className="flex items-center gap-3 mb-5" data-id="element-341" data-mp="15ylsjf70q4ns">
              {brandLogo && <span className="w-11 h-11 rounded-xl bg-white flex items-center justify-center p-1.5 shrink-0" data-id="element-342" data-mp="98mkrmyoza69">
                  <img src={brandLogo} alt={brand} className="max-w-full max-h-full object-contain mix-blend-multiply" data-id="element-343" data-mp="dapzckgnquvr" />
                </span>}
              <div className="text-left" data-id="element-344" data-mp="n0sqsg84s0rz">
                <p className="font-display text-xl text-brand-navy leading-tight" data-id="element-345" data-mp="3lq1bffe6altt">
                  {brand}
                </p>
                <p className="text-xs text-brand-navy/55" data-id="element-346" data-mp="3qn9vjlivszxp">{title}</p>
              </div>
            </div>

            {/* The scannable QR in a clean white card with a scan shimmer */}
            <div className="relative bg-white rounded-3xl p-5 shadow-2xl" data-id="element-347" data-mp="3e5pbxgg64opw">
              <motion.div aria-hidden="true" initial={{
            y: -8
          }} animate={{
            y: 248
          }} transition={{
            duration: 1.8,
            repeat: Infinity,
            repeatType: 'reverse',
            ease: 'easeInOut'
          }} className="absolute left-5 right-5 h-10 bg-gradient-to-b from-brand-blue/30 to-transparent rounded-full blur-sm pointer-events-none" data-id="element-348" data-mp="396m6huwa0ghe" />
              <img src={qrSrc} alt={`Scannable catch pass QR for ${brand}`} width={280} height={280} className="w-[280px] h-[280px] max-w-[72vw] max-h-[72vw] object-contain relative" data-id="element-349" data-mp="2lf8c7jekeats" />
            </div>

            <p className="mt-4 font-mono text-2xl font-bold tracking-[0.3em] text-brand-navy" data-id="element-350" data-mp="thupv6fuebdc">
              {userCode}
            </p>
            <p className="text-xs text-brand-navy/55 mt-1" data-id="element-351" data-mp="1397k24p45dmj">
              Or read this code to staff
            </p>

            {/* Staff-facing instructions */}
            <div className="mt-7 w-full max-w-sm bg-white border border-brand-lavender rounded-[1.5rem] p-5 shadow-[0_10px_24px_rgba(22,38,95,0.06)]" data-id="element-352" data-mp="ych6bo0s6u4a">
              <p className="flex items-center gap-2 text-brand-purple font-bold uppercase tracking-wider text-xs mb-4" data-id="element-353" data-mp="5miclfbn68ta">
                <ScanLine className="w-4 h-4" data-id="element-354" data-mp="kjr5weki9mnn" />
                How to redeem
              </p>
              <ol className="space-y-3" data-id="element-355" data-mp="fj3hm0nnvjx4">
                {[{
              icon: <MapPin className="w-4 h-4" data-id="element-356" data-mp="1vx3fl62y4zhq" />,
              text: `Find a staff member at ${brand}`
            }, {
              icon: <QrCode className="w-4 h-4" data-id="element-357" data-mp="1yikld81g3nmr" />,
              text: 'Hold up this screen so they can see the QR'
            }, {
              icon: <ScanLine className="w-4 h-4" data-id="element-358" data-mp="23iyxb1663hq2" />,
              text: 'Staff scans it to validate & unlock your catch'
            }].map((step, i) => <li key={i} className="flex items-center gap-3 text-brand-navy/80" data-id="element-359" data-mp="1tjzwu5vk2se5" data-mpi={i}>
                    <span className="w-7 h-7 rounded-full bg-brand-purple text-white text-xs font-bold flex items-center justify-center shrink-0" data-id="element-360" data-mp="2ha3qplixzdry">
                      {i + 1}
                    </span>
                    <span className="text-brand-purple shrink-0" data-id="element-361" data-mp="28fz115fjo8e0">
                      {step.icon}
                    </span>
                    <span className="text-sm leading-snug" data-id="element-362" data-mp="2cg96mgzfcwsc">{step.text}</span>
                  </li>)}
              </ol>
            </div>

            <Button onClick={onClose} className="w-full max-w-sm mt-6 bg-white text-brand-navy border-none hover:bg-white/90" data-id="element-363" data-mp="34ywm9gvnjjm1">
              Done
            </Button>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}